import * as React from 'react';

import { Button, Checkbox, Empty, Input, List, Modal, Typography } from 'antd';

import { useCurrencies } from 'v2/hooks/useCurrencies';
import type { Currency } from 'v2/interfaces/currency';

import { Icon } from '@eduzz/ui-antd-theme';

type ChooseCurrenciesModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  selectedCurrencies: Currency[];
  addCurrencies: (currencies: Currency[]) => void;
};

function ChooseCurrenciesModal({
  isOpen,
  closeModal,
  selectedCurrencies: selectedCurrenciesProp = [],
  addCurrencies
}: ChooseCurrenciesModalProps) {
  const [search, setSearch] = React.useState('');
  const currenciesQuery = useCurrencies();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);

  function handleCurrencyClick(currency: Currency) {
    const selectedCurrenciesIds = selectedCurrencies.map(selectedCurrency => selectedCurrency.id);

    if (selectedCurrenciesIds.includes(currency.id)) {
      setSelectedCurrencies(selectedCurrencies.filter(selectedCurrency => selectedCurrency.id !== currency.id));
      return;
    }

    setSelectedCurrencies([...selectedCurrencies, currency]);
  }

  function handleAddButtonClick() {
    setSelectedCurrencies([]);
    addCurrencies(selectedCurrencies);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  function handleClose() {
    setSelectedCurrencies([]);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  const filteredCurrencies = React.useMemo(() => {
    const noBRLCurrencies = currenciesQuery.data?.filter(currency => currency.code !== 'BRL');
    const notAlreadySelectedCurrencies = noBRLCurrencies?.filter(currency => {
      const selectedCurrenciesIds = selectedCurrenciesProp.map(selectedCurrency => selectedCurrency.id);
      return !selectedCurrenciesIds.includes(currency.id);
    });
    if (!search) return notAlreadySelectedCurrencies;
    return notAlreadySelectedCurrencies.filter(currency => {
      const codeMatches = currency.code.toLowerCase().includes(search.toLowerCase());
      const nameMatches = currency.name.toLowerCase().includes(search.toLowerCase());
      return codeMatches || nameMatches;
    });
  }, [currenciesQuery.data, search, selectedCurrenciesProp]);

  const selectedIds = selectedCurrencies.map(currency => currency.id);

  return (
    <Modal
      open={isOpen}
      width={600}
      title='Escolher moedas'
      onCancel={handleClose}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            id='add-currencies-button'
            type='primary'
            onClick={handleAddButtonClick}
            disabled={!selectedCurrencies.length}
          >
            Adicionar moedas {!!selectedCurrencies.length && `(${selectedCurrencies.length})`}
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-4'>
        <Input
          id='search-currencies'
          allowClear
          prefix={<Icon.Search size={16} />}
          placeholder='Buscar moedas'
          value={search}
          onChange={event => setSearch(event.target.value)}
        />

        <div className='max-h-100 overflow-auto rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-2'>
          {filteredCurrencies?.length === 0 ? (
            <Empty description='Nenhuma moeda encontrada' />
          ) : (
            <List
              dataSource={filteredCurrencies}
              renderItem={currency => (
                <List.Item
                  className='cursor-pointer rounded-(--uizz-antd-border-radius) px-3 hover:bg-(--uizz-antd-color-bg-container)'
                  onClick={() => handleCurrencyClick(currency)}
                >
                  <div className='flex items-center gap-2'>
                    <Checkbox
                      id={`currency-${currency.code}`}
                      checked={selectedIds.includes(currency.id)}
                      onClick={e => e.stopPropagation()}
                      onChange={() => handleCurrencyClick(currency)}
                    />
                    <Typography.Text>
                      {currency.code} ({currency.name})
                    </Typography.Text>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export { ChooseCurrenciesModal };
